import React, { useMemo, useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { isApprovedStatus } from './utils';

// Helper function to generate a CSS class name from the theme text
const getThemeClass = (theme) => {
  if (!theme) return '';
  
  // First normalize accented characters
  const normalized = theme.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  // Then normalize theme text: lowercase, replace spaces/commas with hyphens, remove special chars
  const className = normalized
    .toLowerCase()
    .replace(/\s*\,\s*/g, '-') // Replace commas and surrounding spaces with hyphen
    .replace(/\s+/g, '-')       // Replace remaining spaces with hyphen
    .replace(/[^a-z0-9\-]/g, ''); // Remove non-alphanumeric (except hyphens)
  
  return `tema-${className}`;
};

// Helper function to get the step number for the status
const getStatusStep = (status) => {
  if (!status) return 0;
  const lowerStatus = status.toLowerCase().trim();
  if (lowerStatus.startsWith('aberto para consulta pública')) return 1;
  if (lowerStatus.startsWith('consulta pública encerrada')) return 2;
  if (lowerStatus.startsWith('aguardando designação de relator')) return 3;
  if (lowerStatus.startsWith('pronta para pauta na comissão') || lowerStatus.startsWith('pronto para pauta no plenário')) return 4;
  if (lowerStatus.startsWith('aguardando parecer do(a) relator(a)')) return 5;
  if (lowerStatus.startsWith('aprovada') || [
      'sancionada pelo presidente lula', 
      'sancionada pelo presidente jair bolsonaro'
     ].includes(lowerStatus)) return 6;
  if (lowerStatus.startsWith('aguardando')) return 3; // Generic waiting
  return 0; // Default/Unknown
};

// Extracts initials from a name string
const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  // Handle specific non-person cases
  const nonPersonIndicators = ['-', 'CVM', 'Receita Federal', 'CPI', 'Banco Central'];
  if (nonPersonIndicators.some(indicator => name.includes(indicator))) {
    return '🏢'; // Or return null to show no avatar
  }

  // Basic name extraction (remove titles like 'Presidente da República')
  let cleanedName = name.replace(/\([^)]+\)/g, '').trim(); // Remove text in parentheses
  cleanedName = cleanedName.replace(/^(?:Deputado\(a\)|Senador\(a\)|Presidente da República)\s+/i, '').trim(); // Remove common titles

  const names = cleanedName.split(' ').filter(Boolean);
  if (names.length === 0) return '?';
  if (names.length === 1) return names[0][0].toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
};

// Format the politician name to match the filename format
const formatNameForFilepath = (name) => {
  if (!name || typeof name !== 'string') return null;
  
  // Handle specific non-person cases
  const nonPersonIndicators = ['-', 'CVM', 'Receita Federal', 'CPI', 'Banco Central', 'Ainda não definido'];
  if (nonPersonIndicators.some(indicator => name.includes(indicator))) {
    return null; // Not a person, don't try to find image
  }
  
  // Extract the name before any parentheses or party information
  let cleanedName = name;
  
  // Remove party and state information in parentheses - handles formats like (PL/RJ), (PSD-AM), etc.
  cleanedName = cleanedName.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  
  // Remove common prefixes/titles
  cleanedName = cleanedName.replace(/^(?:Deputado\(a\)|Senador\(a\)|Presidente da República|Sen\.|Dep\.|Presidente)\s+/i, '').trim();
  
  // Handle special cases
  if (cleanedName.toLowerCase().includes('lula')) return 'Lula';
  
  // Direct mappings for the problem cases with explicit file extensions
  const exactMappings = {
    'Sidney Leite': ['SidneyLeite', 'png'],
    'Julio Lopes': ['JulioLopes', 'png'],
    'Laura Carneiro': ['LauraCarneiro', 'png'],
    'Delegado Paulo Bilynskyj': ['DelegadoPauloBilynskyj', 'png'],
    'Paulo Bilynskyj': ['DelegadoPauloBilynskyj', 'png'],
    'Aureo Ribeiro': ['AureoRibeiro', 'jpg'],
    'Áureo Ribeiro': ['AureoRibeiro', 'jpg'],
    'Gilberto Abramo': ['GilbertoAbramo', 'png'],
    'Marcelo Crivella': ['MarceloCrivella', 'png'],
    'Hildo Rocha': ['HildoRocha', 'png']
  };
  
  // Check for exact matches with the problem cases
  if (exactMappings[cleanedName]) {
    return { 
      filename: exactMappings[cleanedName][0],
      extension: exactMappings[cleanedName][1]
    };
  }
  
  // Comprehensive mapping of politician names to filenames
  const specialCases = {
    // Existing mappings
    'Caio Vianna': 'CaioVianna',
    'Reginaldo Lopes': 'ReginaldoLopes',
    'Geninho Zuliani': 'GeninhoZuliani',
    'Adriana Ventura': 'AdrianaVentura',
    'Kiko Celeguim': 'KikoCeleguim',
    'Guilherme Boulos': 'GuilhermeBoulos',
    'Otto Alencar Filho': 'OttoAlencarFilho',
    'Ricardo Ayres': 'RicardoAyres',
    'Silas Câmara': 'SilasCamara',
    'Eduardo Braga': 'EduardoBraga',
    'Rodrigo Pacheco': 'RodrigoPacheco',
    'Erika Kokay': 'ErikaKokay',
    'Cleber Verde': 'CleberVerde',
    'Rogério Carvalho': 'RogerioCarvalho',
    'Luizão Goulart': 'LuizaoGoulart',
    'Julia Zanatta': 'JuliaZanatta',
    'Marcos do Val': 'MarcosDoVal',
    'Eros Biondini': 'ErosBiondini',
    'Carlos Chiodini': 'CarlosChiodini',
    'André Figueiredo': 'AndreFigueiredo',
    'Celso Sabino': 'CelsoSabino',
    'Jones Moura': 'JonesMoura',
    'Carlos Veras': 'CarlosVeras',
    'Júlio Arcoverde': 'JulioArcoverde',
    'Augusto Coutinho': 'AugustoCoutinho',
    'Rubens Bueno': 'RubensBueno',
    'Wellington Roberto': 'WellingtonRoberto',
    'João Carlos Bacelar': 'JoaoCarlosBacelar',
    'Bacelar': 'JoaoCarlosBacelar',
    'Esperidião Amin': 'EsperidiaoAmin',
    'Célio Studart': 'CelioStudart',
    'Josenildo': 'Josenildo',
    'Chiquinho Brazão': 'ChiquinhoBrazao',
    'Paulo Teixeira': 'PauloTeixeira',
    'Mario Negromonte Jr': 'MarioNegromonteJr',
    'Mario Negromonte': 'MarioNegromonteJr',
    'Carolini de Toni': 'CarolineDeToni',
    'Caroline de Toni': 'CarolineDeToni',
    'Carlos Portinho': 'CarlosPortinho',
    'Danilo Forte': 'DaniloForte',
    'Eduardo Gomes': 'EduardoGomes',
    'Marcos Pontes': 'MarcosPontes',
    'Hugo Motta': 'HugoMotta',
    'Lucas Ramos': 'LucasRamos',
    'Davi Alcolumbre': 'DaviAlcolumbre',
    'Fabio Schiochet': 'FabioSchiochet',
    'Nely Aquino': 'NelyAquino',
    'Gilson Daniel': 'GilsonDaniel',
  };
  
  if (specialCases[cleanedName]) {
    return { 
      filename: specialCases[cleanedName],
      extension: 'png' // Default extension
    };
  }
  
  // Additional checks for common patterns in politician names
  for (const [key, value] of Object.entries(specialCases)) {
    // Check if the name starts with a known politician's name (to handle cases with suffixes)
    if (cleanedName.startsWith(key + ' ')) {
      return { 
        filename: value,
        extension: 'png' // Default extension
      };
    }
  }
  
  // Normalize: Remove accents, convert to PascalCase
  return { 
    filename: cleanedName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(''),
    extension: 'png' // Default extension
  };
};

// Enhanced avatar component with photo support
const Avatar = ({ sponsorName }) => {
  // Comprehensive direct mappings for all politicians
  const directMappings = {
    // Previously fixed politicians
    'Sidney Leite': 'SidneyLeite.png',
    'Sidney Leite (PSD-AM)': 'SidneyLeite.png',
    'Julio Lopes': 'JulioLopes.png',
    'Julio Lopes (PP-RJ)': 'JulioLopes.png',
    'Laura Carneiro': 'LauraCarneiro.png',
    'Laura Carneiro (PSD-RJ)': 'LauraCarneiro.png',
    'Delegado Paulo Bilynskyj': 'DelegadoPauloBilynskyj.png',
    'Delegado Paulo Bilynskyj (PL-SP)': 'DelegadoPauloBilynskyj.png',
    'Paulo Bilynskyj': 'DelegadoPauloBilynskyj.png',
    'Aureo Ribeiro': 'AureoRibeiro.jpg',
    'Aureo Ribeiro (SOLIDARIEDADE-RJ)': 'AureoRibeiro.jpg',
    'Áureo Ribeiro': 'AureoRibeiro.jpg',
    'Gilberto Abramo': 'GilbertoAbramo.png',
    'Gilberto Abramo (REPUBLICANOS-MG)': 'GilbertoAbramo.png',
    'Marcelo Crivella': 'MarceloCrivella.png',
    'Marcelo Crivella (REPUBLICANOS-RJ)': 'MarceloCrivella.png',
    'Hildo Rocha': 'HildoRocha.png',
    'Hildo Rocha (PMDB-MA)': 'HildoRocha.png',
    
    // PATROCINADOR column
    'Marcos do Val': 'MarcosDoVal.jpg',
    'Eros Biondini': 'ErosBiondini.jpg',
    'Caio Vianna': 'CaioVianna.jpg',
    'Luizão Goulart': 'LuizaoGoulart.png',
    'Reginaldo Lopes': 'ReginaldoLopes.png',
    'Geninho Zuliani': 'GeninhoZuliani.png',
    'Julia Zanatta': 'JuliaZanatta.png',
    'Adriana Ventura': 'AdrianaVentura.png',
    'Kiko Celeguim': 'KikoCeleguim.png',
    'Guilherme Boulos': 'GuilhermeBoulos.png',
    'Otto Alencar Filho': 'OttoAlencarFilho.png',
    'Ricardo Ayres': 'RicardoAyres.png',
    'Silas Câmara': 'SilasCamara.png',
    'Eduardo Braga': 'EduardoBraga.png',
    'Cleber Verde': 'CleberVerde.png',
    'Erika Kokay': 'ErikaKokay.png',
    'Rodrigo Pacheco': 'RodrigoPacheco.png',
    'Rogério Carvalho': 'RogérioCarvalho.png',
    
    // RELATOR column
    'Carlos Portinho': 'CarlosPortinho.png',
    'Carlos Portinho (PL/RJ)': 'CarlosPortinho.png',
    'Gilson Daniel': 'GilsonDaniel.png',
    'Gilson Daniel (PODEMOS/ES)': 'GilsonDaniel.png',
    'Marcos Pontes': 'MarcosPontes.png',
    'Marcos Pontes (PL/SP)': 'MarcosPontes.png',
    
    // RESPONSÁVEL TRAMITAÇÃO column
    'Danilo Forte': 'DaniloForte.png',
    'Danilo Forte (Presidente da Comissão)': 'DaniloForte.png',
    'Mario Negromonte Jr.': 'MarioNegromonteJr.png',
    'Mario Negromonte Jr': 'MarioNegromonteJr.png',
    'Mario Negromonte Jr. (Presidente da Comissão)': 'MarioNegromonteJr.png',
    'Carolini de Toni': 'CarolineDeToni.png',
    'Caroline de Toni': 'CarolineDeToni.png',
    'Carolini de Toni (Presidente da Comissão)': 'CarolineDeToni.png',
    'Nely Aquino': 'NelyAquino.png',
    'Nely Aquino (Presidente da Comissão)': 'NelyAquino.png',
    'Fabio Schiochet': 'FabioSchiochet.png',
    'Fabio Schiochet (Presidente da Comissão)': 'FabioSchiochet.png',
    'Davi Alcolumbre': 'DaviAlcolumbre.png',
    'Davi Alcolumbre (Presidente Senado)': 'DaviAlcolumbre.png',
    'Lucas Ramos': 'LucasRamos.png',
    'Lucas Ramos (Presidente da Comissão)': 'LucasRamos.png',
    'Hugo Motta': 'HugoMotta.png',
    'Hugo Motta (Presidente da Câmara dos Deputados)': 'HugoMotta.png'
  };

  // Handle non-person cases
  if (sponsorName === 'Ainda não definido' || 
      sponsorName === '-' || 
      sponsorName.includes('CVM') || 
      sponsorName.includes('Receita Federal') ||
      sponsorName.includes('CPI') || 
      sponsorName.includes('Banco Central')) {
    return (
      <div className="avatar org-avatar" title={sponsorName}>
        🏢
      </div>
    );
  }

  // Check if we have a direct mapping for this name
  let imagePath = null;
  
  // Try exact match first
  if (directMappings[sponsorName]) {
    imagePath = directMappings[sponsorName];
  } else {
    // Try matching without parentheses
    const nameWithoutParentheses = sponsorName.replace(/\s*\([^)]*\)\s*/g, '').trim();
    if (directMappings[nameWithoutParentheses]) {
      imagePath = directMappings[nameWithoutParentheses];
    } else {
      // Try to match beginning of names
      for (const [key, value] of Object.entries(directMappings)) {
        if (sponsorName.startsWith(key)) {
          imagePath = value;
          break;
        }
      }
    }
  }

  // Generate initials as a fallback
  const initials = getInitials(sponsorName);
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
       hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };
  const colors = ['#ff9800', '#2196f3', '#f44336', '#4caf50', '#9c27b0', '#00bcd4', '#ff5722', '#795548'];
  const colorIndex = Math.abs(hashCode(initials)) % colors.length;
  const bgColor = colors[colorIndex];

  return (
    <div className="avatar-container" title={sponsorName}>
      {imagePath ? (
        <img 
          src={`${process.env.PUBLIC_URL}/fotospoliticos/${imagePath}`}
          className="photo-avatar"
          alt={sponsorName}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <div 
        className="fallback-avatar" 
        style={{ 
          backgroundColor: bgColor,
          display: imagePath ? 'none' : 'flex' // Show initials if no image path
        }}
      >
        {initials}
      </div>
    </div>
  );
};

// Component for the status progress bar
const StatusProgressBar = ({ statusText }) => {
  const step = getStatusStep(statusText);
  const maxSteps = 6;
  const percentage = maxSteps > 0 ? (step / maxSteps) * 100 : 0;

  // Define colors for each step (adjust as needed)
  const stepColors = [
    '#888888', // Step 0 (Unknown/Default)
    '#f44336', // Step 1 (Red)
    '#ff9800', // Step 2 (Orange)
    '#ffc107', // Step 3 (Amber)
    '#cddc39', // Step 4 (Lime)
    '#8bc34a', // Step 5 (Light Green)
    '#4caf50', // Step 6 (Green)
  ];

  const barColor = stepColors[step] || stepColors[0];

  return (
    <div className="status-progress-bar-container" title={statusText}>
      <div className="status-progress-bar" style={{ width: `${percentage}%`, backgroundColor: barColor }}>
        {/* Optional: Could add an indicator element here */}
      </div>
    </div>
  );
};

function DataTable({ data }) {
  const [sorting, setSorting] = useState([]);
  const [approvedRowsCollapsed, setApprovedRowsCollapsed] = useState(true);
  const [autoCollapseComplete, setAutoCollapseComplete] = useState(true);

  // Function to toggle collapse/expand all approved rows at once
  const toggleApprovedRowsCollapse = () => {
    setApprovedRowsCollapsed(!approvedRowsCollapsed);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'Acao',
        header: 'Ação',
        cell: ({ row }) => (
          row.original.link && row.original.link !== '-' ?
            <a href={row.original.link} target="_blank" rel="noopener noreferrer">
              {row.getValue('Acao')}
            </a> :
            row.getValue('Acao')
        ),
      },
      // {
      //   accessorKey: 'Relevancia',
      //   header: 'Relevância',
      // }, // Omitted as requested
      {
        accessorKey: 'Patrocinador',
        header: 'Patrocinador',
        cell: ({ row }) => {
          const sponsorText = row.getValue('Patrocinador') || '-';
          // Handle multiple sponsors separated by comma
          const sponsors = sponsorText.split(/\s*,\s*/);
          
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sponsors.map((sponsor, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Avatar sponsorName={sponsor} />
                  <span>{sponsor}</span>
                </div>
              ))}
              {/* Fallback if splitting resulted in nothing meaningful */} 
              {sponsors.length === 0 && <span>{sponsorText}</span>}
            </div>
          );
        }
      },
      {
        accessorKey: 'PartidoPatrocinador',
        header: 'Partido Patrocinador',
        cell: ({ row }) => {
          const partido = row.getValue('PartidoPatrocinador');
          if (!partido) return '-';
          
          // Abbreviate specific party names
          if (partido === 'REPUBLICANOS') return 'REPUB';
          if (partido === 'SOLIDARIEDADE') return 'SOLID';
          
          // Handle party names with state abbreviations
          if (partido.startsWith('REPUBLICANOS/')) {
            return partido.replace('REPUBLICANOS/', 'REPUB./');
          }
          if (partido.startsWith('SOLIDARIEDADE/')) {
            return partido.replace('SOLIDARIEDADE/', 'SOLID./');
          }
          
          return partido;
        }
      },
      {
        accessorKey: 'OrgaoAtual',
        header: 'Órgão Atual',
        cell: ({ row }) => {
          const orgao = row.getValue('OrgaoAtual');
          if (!orgao) return '-';
          
          // Simplify specific organization names
          if (orgao === 'Câmara dos Deputados') return 'Câmara';
          if (orgao === 'Banco Central do Brasil') return 'Banco Central';
          
          return orgao;
        }
      },
      {
        accessorKey: 'Tema',
        header: 'Tema',
        cell: ({ row }) => {
          const theme = row.getValue('Tema');
          const themeClass = getThemeClass(theme);
          return <span className={themeClass}>{theme}</span>;
        }
      },
      {
        accessorKey: 'Objetivo',
        header: 'Objetivo',
        cell: ({ row }) => (
          <div style={{ maxHeight: '100px', overflowY: 'auto', textAlign: 'left' }}>
            {row.getValue('Objetivo')}
          </div>
        ),
      },
      {
        accessorKey: 'Status',
        header: 'Status',
        cell: ({ row }) => {
           const statusText = row.getValue('Status');
           // Render the progress bar component
           return <StatusProgressBar statusText={statusText} />;
        },
      },
      {
        accessorKey: 'ApresentadaEm',
        header: 'Apresentada em',
      },
      {
        accessorKey: 'UltimaTramitacao',
        header: 'Última Tramitação',
      },
      {
        accessorKey: 'RelatorAtual',
        header: 'Relator Atual',
        cell: ({ row }) => {
          const relator = row.getValue('RelatorAtual');
          if (!relator || relator === '-') return '-';
          
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Avatar sponsorName={relator} />
              <span>{relator}</span>
            </div>
          );
        }
      },
      {
        accessorKey: 'ResponsavelTramitacao',
        header: 'Responsável Tramitação',
        cell: ({ row }) => {
          let responsavel = row.getValue('ResponsavelTramitacao');
          if (!responsavel || responsavel === '-') return '-';
          
          // Replace the long text with a shorter version
          if (responsavel.includes("Responsável ainda não definido // Presidência está vaga")) {
            responsavel = "Ainda não definido";
          }
          
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Avatar sponsorName={responsavel} />
              <span>{responsavel}</span>
            </div>
          );
        }
      },
      // {
      //   accessorKey: 'PartidoResponsavelTramitacao',
      //   header: 'Partido Responsável Tramitação',
      // }, // Omitted as requested
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;
  
  // Group rows by approved status
  const approvedRows = rows.filter(row => isApprovedStatus(row.original.Status));
  const nonApprovedRows = rows.filter(row => !isApprovedStatus(row.original.Status));
  
  // Determine which rows to show based on collapse state
  const visibleRows = approvedRowsCollapsed 
    ? nonApprovedRows 
    : [...approvedRows, ...nonApprovedRows];

  return (
    <div className="table-container">
      <table>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? 'cursor-pointer select-none'
                          : '',
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: ' 🔼',
                        desc: ' 🔽',
                      }[header.column.getIsSorted()] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {/* When collapsed, show a toggle button at the top */}
          {autoCollapseComplete && approvedRowsCollapsed && approvedRows.length > 0 && (
            <tr className="collapsed-indicator">
              <td colSpan={table.getAllColumns().length}>
                <div className="toggle-collapsed-container">
                  <span>{approvedRows.length} propostas aprovadas</span>
                  <button 
                    className="group-toggle-button" 
                    onClick={toggleApprovedRowsCollapse}
                    aria-label="Mostrar propostas aprovadas"
                    title="Mostrar propostas aprovadas"
                  >
                    ▼
                  </button>
                </div>
              </td>
            </tr>
          )}
          
          {/* Render the rows in specific order with collapse button right after approved rows */}
          {!approvedRowsCollapsed && approvedRows.length > 0 && (
            <>
              {/* First render the approved rows */}
              {approvedRows.map(row => (
                <tr 
                  key={row.id} 
                  className="approved-row"
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
              
              {/* Then render the collapse button immediately after the approved rows */}
              {autoCollapseComplete && (
                <tr className="expand-indicator">
                  <td colSpan={table.getAllColumns().length}>
                    <div className="toggle-expanded-container">
                      <button 
                        className="group-toggle-button" 
                        onClick={toggleApprovedRowsCollapse}
                        aria-label="Colapsar propostas aprovadas"
                        title="Colapsar propostas aprovadas"
                      >
                        ▲
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </>
          )}
          
          {/* Finally render the non-approved rows */}
          {nonApprovedRows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable; 